/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { useEuiTheme } from '../../services';
import { euiFacetGroupStyles } from './facet_group.styles';
export var LAYOUTS = ['vertical', 'horizontal'];
export var GUTTER_SIZES = ['none', 's', 'm', 'l'];
export var EuiFacetGroup = ({
  children,
  className,
  layout = 'vertical',
  gutterSize = 'm',
  ...rest
}) => {
  const theme = useEuiTheme();
  const styles = euiFacetGroupStyles(theme, layout);
  const cssStyles = [styles.euiFacetGroup, styles[gutterSize], styles[layout]];
  const classes = classNames('euiFacetGroup', className);
  return <div className={classes} css={cssStyles} {...rest}>
      {children}
    </div>;
};
EuiFacetGroup.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,

  /**
       * Vertically in a column, or horizontally in one wrapping line
       */
  layout: PropTypes.any,

  /**
       * Distance between facet buttons.
       * Horizontal layout always adds more distance horizontally between buttons.
       */
  gutterSize: PropTypes.any
};

try {
  EuiFacetGroup.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/facet/facet_group.tsx',
    description: '',
    displayName: 'EuiFacetGroup',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      layout: {
        defaultValue: {
          value: 'vertical'
        },
        description: 'Vertically in a column, or horizontally in one wrapping line',
        name: 'layout',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/facet/facet_group.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"horizontal" | "vertical"',
          value: [{
            value: '"horizontal"'
          }, {
            value: '"vertical"'
          }]
        }
      },
      gutterSize: {
        defaultValue: {
          value: 'm'
        },
        description: 'Distance between facet buttons.\n' + 'Horizontal layout always adds more distance horizontally between buttons.',
        name: 'gutterSize',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/facet/facet_group.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "m" | "l" | "none"',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }, {
            value: '"none"'
          }]
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}